import React, { useCallback, useEffect, useMemo, useLayoutEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import MaterialTooltip from '@mui/material/Tooltip';
import truncate from 'lodash/truncate';
import { useSelector } from 'react-redux';

import { useMountedEffect } from '@clatter/platform';
import { CircularLoader } from '../../../shared';
import { viewStateSelectors } from '../../../store';
import { getIconForMimeType } from '../../../helpers';
import { NgCard, NgCardContent, NgTooltip } from '../../UI';
import { useDnDSortableItem, useItemThumbnail } from '../../../hooks';
import { FolderManagerActionTypes, FolderManagerItemTypes, FolderManagerMimeTypes } from '../../../interfaces';

import { ShortcutIcon } from './ShortcutIcon';
import { FileDropContainer } from './FileDropContainer';
import { FolderManagerItemActions } from './FolderManagerItemActions';

//region INTERFACES
interface FolderManagerGridItemProps {
  item: FolderManagerItem;
  selectedRows?: FolderManagerItem[];
  overId?: string | number | undefined | null;
  isUploadDisabled?: boolean;
  hideActions?: boolean;
  disableDnD?: boolean;
  testId?: string;
  acceptedDragTypes?: string[];
  thumbnailAssetId?: number; // optional asset ID to use for thumbnail instead of item.id
  showDeletedWarning?: boolean;
  viewStateKey?: string;
  onSelect?: (data: { event: any; item: any }) => void;
  onAction?: (data: any) => void;
  onDoubleClick?: (data: any) => void;
  onClick?: (data: any) => void;
}

interface GridItemContentProps {
  item: FolderManagerItem;
  isSelected: boolean;
  isOver: boolean;
  setNodeRef: any;
  itemStyle: any;
  attributes: any;
  listeners: any;
  shouldDisableDnD: boolean;
  isUploadDisabled?: boolean;
  hideActions?: boolean;
  acceptedDragTypes?: string[];
  thumbnailAssetId?: number;
  showDeletedWarning?: boolean;
  viewStateKey?: string;
  testId?: string;
  onSelect?: (data: { event: any; item: any }) => void;
  onAction?: (data: any) => void;
  onDoubleClick?: (data: any) => void;
  onClick?: (data: any) => void;
}

interface HeaderTitleProps {
  title: string;
}
//endregion

//region STYLED
const StyledGridViewItem = styled.li<{ $isNotAccepted?: boolean }>`
  user-select: none;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: auto;
  min-width: 0;

  border-radius: ${({ theme }) => theme.radius.small};

  ${({ $isNotAccepted }) =>
    $isNotAccepted &&
    `
    display: none;
    opacity: 0.5;
    cursor: not-allowed !important;

    &:hover {
      cursor: not-allowed !important;
    }
  `}

  .item-header {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;

    .title-container {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      gap: 1rem;
      flex: 1;
    }

    .header-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      overflow: hidden;

      h4 {
        margin: 0 0 4px;
      }
    }

    .folder-actions {
      display: flex;
      justify-content: flex-end;
    }
  }

  .item-content {
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    height: auto;

    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }

    .image-loader {
      height: 100%;
      width: 100%;
      min-height: 138px;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  .item-footer {
    display: flex;
    justify-content: flex-end;
  }

  &.over {
    > div:first-of-type {
      border: 2px solid blue;
      border-radius: ${({ theme }) => theme.radius.normal};
    }
  }

  &:hover {
    cursor: pointer;

    > div:first-of-type {
      background-color: ${({ theme }) => theme.palette.hoverBackgroundColor};
    }

    .show-on-hover {
      display: inline-block;
    }
  }

  background-color: transparent;

  &.selected {
    outline: ${({ theme }) => `2px solid ${theme.palette.black}`};
    outline-offset: -2px;

    > div:first-of-type {
      background-color: ${({ theme }) => theme.palette.selectedBackgroundColor};
    }
  }

  &.is-folder {
    .item-header {
      h4 {
        top: 2px;
      }
    }

    .item-content {
      display: none;
    }
  }

  &.orphaned {
    opacity: 0.5;
  }

  &.deleted {
    > div:first-of-type {
      background-color: rgba(244, 67, 54, 0.1);
      border: 1px solid rgba(244, 67, 54, 0.3);
    }

    &:hover {
      > div:first-of-type {
        background-color: rgba(244, 67, 54, 0.15);
      }
    }

    &.selected {
      > div:first-of-type {
        background-color: rgba(244, 67, 54, 0.2);
        outline: ${({ theme }) => `2px solid ${theme.palette.black}`};
      }
    }
  }
`;
//endregion

const ThumbnailContainer = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: ${({ theme }) => theme.radius.small};
  background-color: ${({ theme }) => theme.palette.grey};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ThumbnailImage = styled.img`
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 8px;
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  opacity: 0.7;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 100%;
    height: 100%;
  }
`;

export const ThumbnailOrIcon = ({ thumbnailUrl, item, mimeTypeIcon }: any) => {
  const hasThumbnail = Boolean(thumbnailUrl);

  return hasThumbnail ? (
    <ThumbnailImage src={thumbnailUrl} alt={`Thumbnail of ${item?.displayName || item?.name}`} />
  ) : (
    <ThumbnailContainer>
      <IconWrapper>{mimeTypeIcon || getIconForMimeType(item?.type, item?.isShared)}</IconWrapper>
    </ThumbnailContainer>
  );
};

//region COMPONENTS
const HeaderTitle: React.FC<HeaderTitleProps> = ({ title }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [truncatedTitle, setTruncatedTitle] = useState('');

  const computeTruncation = useCallback(() => {
    const el = containerRef.current;

    if (!el) return truncate(title, { length: 25, omission: '...' });

    const containerWidth = el.offsetWidth;

    const charLimit = Math.max(3, Math.floor(containerWidth / 8));

    return truncate(title, { length: charLimit, omission: '...' });
  }, [title]);

  // do measurements on mount and whenever `title` changes
  useLayoutEffect(() => {
    setTruncatedTitle(computeTruncation());
  }, [computeTruncation]);

  // recompute on container size changes and window resize
  useEffect(() => {
    const el = containerRef.current;
    const onResize = () => setTruncatedTitle(computeTruncation());

    const ro = new ResizeObserver(onResize);
    if (el) ro.observe(el);

    window.addEventListener('resize', onResize);

    return () => {
      if (el) ro.unobserve(el);
      window.removeEventListener('resize', onResize);
    };
  }, [computeTruncation]);

  return (
    <div ref={containerRef} className="header-title">
      <MaterialTooltip title={title} placement="top-start">
        <h4>{truncatedTitle}</h4>
      </MaterialTooltip>
    </div>
  );
};

const isItemSelected = (item: FolderManagerItem, selectedRows: FolderManagerItem[] = []) =>
  selectedRows.some((row) => {
    if ((item as any).instanceId && (row as any).instanceId) {
      return (item as any).instanceId === (row as any).instanceId;
    }
    return row.id === item.id && row.type === item.type;
  });

const getTooltipMessage = (isDeleted: boolean, isTrashed: boolean) => {
  if (isDeleted) {
    return 'This asset has been permanently deleted. It will be automatically removed from this presentation.';
  }
  if (isTrashed) {
    return 'This asset has been moved to trash. You can restore it, or the slide will be unavailable if the asset is permanently deleted';
  }
  return '';
};

// memoized component that contain expensive hooks,
// it's needed so we can avoid costly dnd-kit rerenders
const FolderManagerGridItemContent = React.memo(
  ({
    item,
    isSelected,
    isOver,
    setNodeRef,
    itemStyle,
    attributes,
    listeners,
    shouldDisableDnD,
    isUploadDisabled = false,
    hideActions = false,
    acceptedDragTypes,
    thumbnailAssetId,
    showDeletedWarning = false,
    viewStateKey,
    testId = 'folder-manager-grid-item',
    onSelect,
    onAction,
    onDoubleClick,
    onClick,
  }: GridItemContentProps) => {
    const selectedThemeId = useSelector(viewStateSelectors.getSelectedThemeId(viewStateKey || ''));
    const visibilityRef = useRef<HTMLLIElement>(null);
    const isVisibleRef = useRef(false);
    const selectedThemeIdRef = useRef(selectedThemeId);
    selectedThemeIdRef.current = selectedThemeId;

    const [hasBeenVisible, setHasBeenVisible] = useState(false);

    // only update the themeId passed to the query when the item is in the viewport
    // this prevents offscreen items from refetching when theme changes globally
    const [activeThemeId, setActiveThemeId] = useState(selectedThemeId);

    // sync theme when it changes while item is visible
    useEffect(() => {
      if (isVisibleRef.current && activeThemeId !== selectedThemeId) {
        setActiveThemeId(selectedThemeId);
      }
    }, [selectedThemeId]);

    const isFolder = item.type === FolderManagerItemTypes.FOLDER || item.type === FolderManagerItemTypes.WORKSPACE;
    const isPresentationAsset = item.type === FolderManagerItemTypes.PRESENTATION;

    const assetIdForQuery = thumbnailAssetId || (item as any)?.originalId || item?.id;

    const isTypeAccepted = !acceptedDragTypes || isFolder || acceptedDragTypes.includes(item.type);
    const isTrashed = Boolean(item.trashedAt);
    const isDeleted = Boolean(item.deletedAt);
    const isUnavailable = showDeletedWarning && (isTrashed || isDeleted);

    const firstSlide = isPresentationAsset ? item.slides?.[0] : null;
    const shouldSkipThumbnail = isPresentationAsset && !thumbnailAssetId && !firstSlide;

    const {
      thumbnailUrl,
      isLoading: isThumbnailLoading,
      isFetching: isThumbnailFetching,
      isThemeChanging,
      refetch: refetchThumbnail,
    } = useItemThumbnail({
      itemId: assetIdForQuery,
      itemType: shouldSkipThumbnail ? undefined : isPresentationAsset ? FolderManagerMimeTypes.SLIDE : item.type,
      thumbnailUrl: firstSlide?.thumbnailUrl || item.thumbnailUrl,
      themeId: isPresentationAsset ? item.themeId : activeThemeId,
      assetId: thumbnailAssetId,
      enabled: hasBeenVisible,
    });

    const mimeTypeIcon = useMemo(
      () => getIconForMimeType(item?.type, item?.isShared, item?.isSlideRepositoryFolder),
      [item?.type, item?.isShared, item?.isSlideRepositoryFolder],
    );

    const combinedRef = useCallback(
      (node: HTMLLIElement | null) => {
        visibilityRef.current = node;
        if (!shouldDisableDnD && setNodeRef) {
          setNodeRef(node);
        }
      },
      [shouldDisableDnD, setNodeRef],
    );

    const handleSelect = (event: any) => {
      if (!isTypeAccepted) {
        return;
      }
      // prevent text selection only when shift key is pressed
      if (event.shiftKey) {
        event.preventDefault();
      }
      if (onSelect) {
        onSelect({ event, item });
      }
    };

    const handleClick = (event: any) => {
      if (!isTypeAccepted) {
        return;
      }
      if (onClick) {
        onClick({ event, item });
      } else {
        handleSelect(event);
      }
    };

    const handleDoubleClick = (event: any) => {
      if ((item?.trashedAt || item?.deletedAt) && !isFolder) {
        return;
      }

      if (onDoubleClick) {
        onDoubleClick({ event, item });
      }
    };

    // let's properly refetch thumbnail url after asset's prop change
    useMountedEffect(() => {
      if (!shouldSkipThumbnail && refetchThumbnail && thumbnailUrl !== undefined) {
        refetchThumbnail();
      }
    }, [shouldSkipThumbnail, item?.id]);

    // track visibility via ref (no re-renders) and gate initial fetch via state
    useEffect(() => {
      const element = visibilityRef.current;
      if (!element) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          isVisibleRef.current = entry.isIntersecting;

          if (entry.isIntersecting) {
            // gate initial thumbnail fetch
            if (!hasBeenVisible) {
              setHasBeenVisible(true);
            }
            // sync stale theme when scrolling back into view
            setActiveThemeId((prev) => {
              const current = selectedThemeIdRef.current;
              return prev !== current ? current : prev;
            });
          }
        },
        { rootMargin: '200px' },
      );

      observer.observe(element);
      return () => observer.disconnect();
    }, []);

    const gridItem = (
      <StyledGridViewItem
        ref={combinedRef}
        style={shouldDisableDnD ? {} : itemStyle}
        $isNotAccepted={!isTypeAccepted && !isFolder}
        {...(shouldDisableDnD ? {} : attributes)}
        {...(shouldDisableDnD ? {} : listeners)}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        className={classNames({
          over: isOver,
          selected: isSelected,
          'is-folder': item.type === FolderManagerMimeTypes.FOLDER || item.type === FolderManagerMimeTypes.WORKSPACE,
          orphaned: item.sourceId && !item.source,
          deleted: isUnavailable,
        })}
        data-test-id={testId}
      >
        <NgCard>
          <NgCardContent>
            {item?.sourceId && <ShortcutIcon />}

            <div className="item-header">
              <div className="title-container">
                <div className="hedaer-icon">{mimeTypeIcon}</div>

                <HeaderTitle title={item?.displayName || item?.name} />
              </div>

              <div className="folder-actions">
                {!hideActions && <FolderManagerItemActions item={item} onAction={onAction} />}
              </div>
            </div>

            <div className="item-content">
              {isThumbnailLoading || (isThemeChanging && isThumbnailFetching) ? (
                <div className="image-loader">
                  <CircularLoader />
                </div>
              ) : (
                <ThumbnailOrIcon thumbnailUrl={thumbnailUrl} icon={mimeTypeIcon} item={item} />
              )}
            </div>
          </NgCardContent>
        </NgCard>
      </StyledGridViewItem>
    );

    const content = isUnavailable ? (
      <NgTooltip title={getTooltipMessage(isDeleted, isTrashed)} placement="top">
        {gridItem}
      </NgTooltip>
    ) : (
      gridItem
    );

    // wrap in FileDropContainer only if upload is enabled and onAction is provided
    if (!isUploadDisabled && onAction) {
      return (
        <FileDropContainer
          isItemContainer
          isDisabled={isUploadDisabled || item.type === FolderManagerMimeTypes.SLIDE}
          onUpload={(fileList) =>
            onAction({
              actionType: FolderManagerActionTypes.UPLOAD,
              payload: { files: fileList, item: item },
            })
          }
        >
          {content}
        </FileDropContainer>
      );
    }

    return content;
  },
  (prev, next) => {
    // compare booleans from DnD
    if (prev.isSelected !== next.isSelected) return false;
    if (prev.isOver !== next.isOver) return false;

    // compare scalar / reference-stable props
    if (prev.item !== next.item) return false;
    if (prev.shouldDisableDnD !== next.shouldDisableDnD) return false;
    if (prev.isUploadDisabled !== next.isUploadDisabled) return false;
    if (prev.hideActions !== next.hideActions) return false;
    if (prev.acceptedDragTypes !== next.acceptedDragTypes) return false;
    if (prev.thumbnailAssetId !== next.thumbnailAssetId) return false;
    if (prev.showDeletedWarning !== next.showDeletedWarning) return false;
    if (prev.viewStateKey !== next.viewStateKey) return false;
    if (prev.testId !== next.testId) return false;

    // skip: callbacks (onSelect, onAction, etc.) — identity may change each render
    // skip: setNodeRef, itemStyle, attributes, listeners — stable refs from dnd-kit

    return true;
  },
);

// outer wrapper — subscribes to dnd-kit context via useDnDSortableItem
// re-renders from dnd-kit context are cheap because this
// component only runs the DnD hook and a selection check.
export const FolderManagerGridItem = ({
  item,
  selectedRows = [],
  overId,
  isUploadDisabled,
  hideActions,
  disableDnD = false,
  acceptedDragTypes,
  thumbnailAssetId,
  showDeletedWarning,
  viewStateKey,
  testId,
  onSelect,
  onAction,
  onDoubleClick,
  onClick,
}: FolderManagerGridItemProps) => {
  const isFolder = item.type === FolderManagerItemTypes.FOLDER || item.type === FolderManagerItemTypes.WORKSPACE;
  const isTypeAccepted = !acceptedDragTypes || isFolder || acceptedDragTypes.includes(item.type);
  const shouldDisableDnD = disableDnD || !isTypeAccepted;

  const dndHooks = useDnDSortableItem({
    item: item,
    overId: overId,
    selectedRows: selectedRows,
    thumbnailUrl: undefined, // thumbnail is resolved inside the content component
  });

  const manualIsSelected = shouldDisableDnD ? isItemSelected(item, selectedRows) : false;

  const { isOver, isSelected, listeners, attributes, setNodeRef, itemStyle } = shouldDisableDnD
    ? {
        isOver: false,
        isSelected: manualIsSelected,
        listeners: {},
        attributes: {},
        setNodeRef: null,
        itemStyle: {},
      }
    : dndHooks!;

  return (
    <FolderManagerGridItemContent
      item={item}
      isSelected={isSelected}
      isOver={isOver}
      setNodeRef={setNodeRef}
      itemStyle={itemStyle}
      attributes={attributes}
      listeners={listeners}
      shouldDisableDnD={shouldDisableDnD}
      isUploadDisabled={isUploadDisabled}
      hideActions={hideActions}
      acceptedDragTypes={acceptedDragTypes}
      thumbnailAssetId={thumbnailAssetId}
      showDeletedWarning={showDeletedWarning}
      viewStateKey={viewStateKey}
      testId={testId}
      onSelect={onSelect}
      onAction={onAction}
      onDoubleClick={onDoubleClick}
      onClick={onClick}
    />
  );
};
//endregion
